/*
================================================================================
| FILENAME: src/features/integrations/HubSpotQueryTester.jsx (Definitive & Corrected)
| PURPOSE: To provide a stable, multi-step tool for finding HubSpot target
|          accounts by kicking off a background job.
|
| KEY FIXES:
| 1.  This component has been renamed internally to `HubSpotQueryTester` to
|     match the filename and how it's imported in MainLayout.jsx.
| 2.  It correctly accepts the `setActiveView` function as a prop for navigation,
|     resolving the previous `useNavigate` and blank screen errors.
| 3.  The workflow is a stable two-step process: AI expands titles, then the
|     user creates a Data Hub table via a background job.
================================================================================
*/
import React, { useState } from 'react';
import apiService from '../../services/apiService';
import { Loader, Wand2, Search, CheckCircle } from 'lucide-react';

const BuyingGroupInput = ({ label, value, onChange }) => (
    <div>
        <label className="block text-sm font-semibold text-slate-300 mb-2">{label}</label>
        <textarea
            value={value}
            onChange={onChange}
            className="w-full h-24 p-2 bg-slate-900 border border-slate-700 rounded-md text-sm"
            placeholder="Enter comma-separated job titles..."
        />
    </div>
);

const HubSpotQueryTester = ({ setActiveView }) => {
    const [step, setStep] = useState(1);
    const [initialTitles, setInitialTitles] = useState({ decisionMakers: 'CEO, VP of Sales', influencers: 'Marketing Manager', users: 'Product Manager, Engineer' });
    const [expandedTitlesPayload, setExpandedTitlesPayload] = useState(null);
    const [tableName, setTableName] = useState('New Target Account List');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const handleTitleChange = (persona, value) => {
        setInitialTitles(prev => ({ ...prev, [persona]: value }));
    };
    
    const handleExpandTitles = async () => {
        setIsLoading(true);
        setError('');
        const committee = {
            decisionMakers: initialTitles.decisionMakers.split(',').map(t => t.trim()).filter(Boolean),
            influencers: initialTitles.influencers.split(',').map(t => t.trim()).filter(Boolean),
            users: initialTitles.users.split(',').map(t => t.trim()).filter(Boolean),
        };
        try {
            const result = await apiService.expandHubspotTitles(committee);
            setExpandedTitlesPayload(result);
            setStep(2);
        } catch (err) {
            setError(err.message || 'Failed to expand titles.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateTable = async () => {
        setIsLoading(true);
        setError('');
        try {
            await apiService.createHubspotTargetTable({
                tableName,
                expandedTitles: expandedTitlesPayload,
                apiPath: '/crm/v3/objects/contacts/search'
            });
            // Use the passed-in setActiveView function to navigate
            setActiveView('dataHub'); 
        } catch (err) {
            setError(err.message || 'Failed to start job.');
            setIsLoading(false);
        }
    };

    return (
        <main className="p-8 text-white">
            <h1 className="text-4xl font-bold mb-2">HubSpot Target Account Finder</h1>
            <p className="text-slate-300 text-lg mb-8">Use AI to expand search criteria and find complete buying committees in HubSpot.</p>
            
            <div className="space-y-6">
                <div className="bg-[#0A142C] p-6 rounded-lg border border-slate-700">
                    <h2 className="text-2xl font-bold mb-4 flex items-center">
                        {step > 1 && <CheckCircle className="text-green-500 mr-3" />}
                        Step 1: Define Buying Committee
                    </h2>
                    {step === 1 && (
                        <>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                                <BuyingGroupInput label="Decision Makers" value={initialTitles.decisionMakers} onChange={e => handleTitleChange('decisionMakers', e.target.value)} />
                                <BuyingGroupInput label="Influencers / Champions" value={initialTitles.influencers} onChange={e => handleTitleChange('influencers', e.target.value)} />
                                <BuyingGroupInput label="Users" value={initialTitles.users} onChange={e => handleTitleChange('users', e.target.value)} />
                            </div>
                            <button onClick={handleExpandTitles} disabled={isLoading} className="btn-primary w-full flex items-center justify-center py-3">
                                {isLoading ? <Loader className="animate-spin mr-2" /> : <Wand2 className="mr-2" />}
                                {isLoading ? 'Expanding Titles...' : 'Step 1: Expand Titles with AI'}
                            </button>
                        </>
                    )}
                </div>

                {step >= 2 && (
                     <div className="bg-[#0A142C] p-6 rounded-lg border border-slate-700">
                        <h2 className="text-2xl font-bold mb-4 flex items-center">
                            {step > 2 && <CheckCircle className="text-green-500 mr-3" />}
                            Step 2: Generate Target Table
                        </h2>
                        {step === 2 && (
                            <>
                                <pre className="bg-black p-4 rounded-lg text-xs text-cyan-400 font-mono overflow-auto h-48 border border-slate-700 mb-4" aria-label="AI-generated HubSpot filter payload">
                                    {JSON.stringify(expandedTitlesPayload, null, 2)}
                                </pre>
                                <div>
                                    <label htmlFor="tableName" className="block text-sm font-medium text-slate-300 mb-1">New Table Name</label>
                                    <input
                                        id="tableName"
                                        type="text"
                                        value={tableName}
                                        onChange={e => setTableName(e.target.value)}
                                        className="w-full bg-slate-900 border border-slate-700 text-white text-sm rounded-lg p-2.5 font-mono"
                                    />
                                </div>
                                <button onClick={handleCreateTable} disabled={isLoading || !tableName} className="btn-primary w-full flex items-center justify-center py-3 mt-4">
                                    {isLoading ? <Loader className="animate-spin mr-2"/> : <Search className="mr-2"/>}
                                    {isLoading ? 'Starting Job...' : 'Create Target Account Table'}
                                </button>
                            </>
                        )}
                    </div>
                )}
                
                {error && <div className="mt-4 text-red-400 p-4 bg-red-900/50 rounded-lg">{error}</div>}
            </div>
        </main>
    );
};

export default HubSpotQueryTester;